/**
 * ProductCard.js
 *
 * Controlador global del product card universal.
 *
 * Responsabilidades:
 * - leer configuración desde [data-product-card-scope]
 * - slider / hover / static
 * - Flickity
 * - imagen inicial
 * - máximo de medias
 * - videos
 * - arrows
 * - dots
 * - contador
 * - swatches
 * - cambio de producto/color
 * - wishlist
 * - CTA
 * - precio normal y flotante
 *
 * NO depende de una sección concreta.
 */

(function initializeProductCard(global) {
    "use strict";

    const CARD_SELECTOR = "[data-product-card]";
    const SCOPE_SELECTOR = "[data-product-card-scope]";
    const CONFIG_SELECTOR = "[data-product-card-config]";

    const INSTANCE_KEY = "__productCardInstance";

    const DEFAULT_CONFIG = {
        imageInteraction: "slider",

        initialImage: 1,
        maxImages: 6,

        showVideo: true,

        showArrowsDesktop: true,
        showArrowsMobile: false,

        showDotsDesktop: false,
        showDotsMobile: true,

        maxSwatches: 3,
        showRemainingSwatches: true,
        swatchHoverMedia: true,

        ctaContent: "icon",
        ctaText: "Agregar",
        ctaIcon: "",

        floatingPriceIcon: "",
    };


    /* ==========================================================================
       ProductCard
       ========================================================================== */

    class ProductCard {
        constructor(root) {
            if (!(root instanceof Element)) {
                throw new TypeError(
                    "ProductCard requiere un elemento raíz válido."
                );
            }

            this.root = root;

            this.scope =
                root.closest(SCOPE_SELECTOR);

            this.config =
                this.readConfig();

            this.media =
                root.querySelector(
                    "[data-product-card-media]"
                );

            this.mediaGroups =
                Array.from(
                    root.querySelectorAll(
                        "[data-product-card-media-group]"
                    )
                );

            this.activeMediaGroup =
                this.getActiveMediaGroup();

            this.flickityByGroup =
                new Map();

            this.activeFlickity = null;

            this.selectedSwatch = null;

            this.hoveredSwatch = null;

            this.ctaOriginalParent = null;
            this.ctaOriginalNextSibling = null;

            this.isInitialized = false;
            this.isDestroyed = false;

            this.boundHandleClick =
                this.handleClick.bind(this);

            this.boundHandleMouseOver =
                this.handleMouseOver.bind(this);

            this.boundHandleMouseOut =
                this.handleMouseOut.bind(this);

            this.boundHandleResize =
                this.handleResize.bind(this);

            this.boundHandleKeydown =
                this.handleKeydown.bind(this);
        }


        /* ==========================================================================
           Helpers
           ========================================================================== */

        normalizeString(value) {
            return String(value ?? "").trim();
        }


        normalizeProductId(value) {
            const normalized =
                this.normalizeString(value);

            if (!normalized) {
                return "";
            }

            return normalized
                .split("/")
                .filter(Boolean)
                .at(-1);
        }

        getTrackingType() {
            return this.normalizeString(
                this.root.dataset.nostoTrack ||
                this.root.dataset.productSource ||
                ""
            ).toLowerCase();
        }


        getAttributionKey() {
            return this.normalizeString(
                this.root.dataset
                    .nostoAttributionKey ||
                this.root.dataset
                    .nostoResultId ||
                ""
            );
        }


        buildTrackedUrl(
            productUrl
        ) {
            if (!productUrl) {
                return productUrl;
            }

            try {
                const url =
                    new URL(
                        productUrl,
                        global.location.origin
                    );


                const track =
                    this.getTrackingType();


                const attributionKey =
                    this.getAttributionKey();


                /*
                 * ================================================================
                 * Nosto recommendations
                 * ================================================================
                 */

                if (
                    attributionKey &&
                    (
                        track ===
                        "nosto-recommendation" ||
                        track ===
                        "recommendation" ||
                        track ===
                        "carousel-recommendation"
                    )
                ) {
                    url.searchParams.set(
                        "nosto",
                        attributionKey.replace(
                            /^\?nosto=/,
                            ""
                        )
                    );
                }


                /*
                 * ================================================================
                 * Nosto Search
                 * ================================================================
                 */

                if (
                    track === "serp" ||
                    track === "autocomplete" ||
                    track === "nosto-search" ||
                    track === "search"
                ) {
                    url.searchParams.set(
                        "fromnostosearch",
                        "true"
                    );
                }


                /*
                 * ================================================================
                 * Nosto Category / PLP
                 * ================================================================
                 */

                if (
                    track === "category" ||
                    track === "nosto-category"
                ) {
                    url.searchParams.set(
                        "fromnostocategory",
                        "true"
                    );
                }


                return (
                    url.pathname +
                    url.search +
                    url.hash
                );

            } catch (error) {
                console.warn(
                    "[ProductCard] No fue posible construir la URL con tracking.",
                    error
                );

                return productUrl;
            }
        }


        clamp(value, min, max) {
            return Math.min(
                Math.max(value, min),
                max
            );
        }


        isMobile() {
            return global
                .matchMedia(
                    "(max-width: 749px)"
                )
                .matches;
        }


        getFlickityClass() {
            return (
                global.theme?.Flickity ||
                global.Flickity ||
                null
            );
        }


        /* ==========================================================================
           Configuration
           ========================================================================== */

        readConfig() {
            if (!this.scope) {
                return {
                    ...DEFAULT_CONFIG,
                };
            }

            const configElement =
                this.scope.querySelector(
                    CONFIG_SELECTOR
                );

            if (!configElement) {
                return {
                    ...DEFAULT_CONFIG,
                };
            }

            try {
                const parsed =
                    JSON.parse(
                        configElement.textContent || "{}"
                    );

                return {
                    ...DEFAULT_CONFIG,
                    ...parsed,
                };
            } catch (error) {
                console.error(
                    "[ProductCard] No fue posible leer la configuración.",
                    error
                );

                return {
                    ...DEFAULT_CONFIG,
                };
            }
        }


        refreshConfig() {
            this.scope =
                this.root.closest(
                    SCOPE_SELECTOR
                );

            this.config =
                this.readConfig();
        }


        /* ==========================================================================
           Initialization
           ========================================================================== */

        initialize() {
            if (
                this.isInitialized ||
                this.isDestroyed
            ) {
                return this;
            }

            this.refreshConfig();

            this.root.addEventListener(
                "click",
                this.boundHandleClick
            );

            this.root.addEventListener(
                "mouseover",
                this.boundHandleMouseOver
            );

            this.root.addEventListener(
                "mouseout",
                this.boundHandleMouseOut
            );

            this.root.addEventListener(
                "keydown",
                this.boundHandleKeydown
            );

            document.addEventListener(
                "theme:resize:width",
                this.boundHandleResize
            );

            global.addEventListener(
                "resize",
                this.boundHandleResize
            );

            this.setupSwatches();

            this.applyTrackingToExistingLinks();

            this.activeMediaGroup =
                this.getActiveMediaGroup() ||
                this.mediaGroups[0] ||
                null;

            if (this.activeMediaGroup) {
                this.activateMediaGroup(
                    this.activeMediaGroup,
                    {
                        resetPosition: true,
                    }
                );
            }

            this.setupCTA();
            this.setupFloatingPriceIcon();

            this.isInitialized = true;

            this.root.classList.add(
                "is-product-card-ready"
            );

            return this;
        }


        /* ==========================================================================
           Media groups
           ========================================================================== */

        getActiveMediaGroup() {
            return (
                this.root.querySelector(
                    "[data-product-card-media-group].is-active"
                ) ||
                null
            );
        }


        findMediaGroupByProductId(
            productId
        ) {
            const normalizedProductId =
                this.normalizeProductId(
                    productId
                );

            let group =
                this.mediaGroups.find(
                    (item) =>
                        this.normalizeProductId(
                            item.dataset.productId
                        ) === normalizedProductId
                ) ||
                null;

            if (group) {
                return group;
            }

            /*
             * Los media groups de siblings viven inicialmente dentro de
             * <template>. Solo los materializamos cuando el usuario hace
             * hover/click sobre ese color.
             */
            const template =
                Array.from(
                    this.root.querySelectorAll(
                        "[data-product-card-media-template]"
                    )
                ).find(
                    (item) =>
                        this.normalizeProductId(
                            item.dataset.productId
                        ) === normalizedProductId
                );

            if (!template || !this.media) {
                return null;
            }

            const fragment =
                template.content.cloneNode(true);

            group =
                fragment.querySelector(
                    "[data-product-card-media-group]"
                );

            if (!group) {
                return null;
            }

            const groupsContainer =
                this.root.querySelector(
                    "[data-product-card-media-groups]"
                ) || this.media;

            groupsContainer.appendChild(
                fragment
            );

            this.mediaGroups.push(
                group
            );

            template.remove();

            return group;
        }


        getMediaTrack(group) {
            return (
                group?.querySelector(
                    "[data-product-card-track]"
                ) ||
                null
            );
        }


        getAllSlides(group) {
            return Array.from(
                group?.querySelectorAll(
                    "[data-product-card-slide]"
                ) || []
            );
        }


        getSlides(group) {
            return this.getAllSlides(
                group
            ).filter(
                (slide) =>
                    !slide.dataset
                        .productCardExcluded
            );
        }


        /* ==========================================================================
           Media filtering
           ========================================================================== */

        prepareMediaGroup(group) {
            if (!group) {
                return;
            }

            const allSlides =
                this.getAllSlides(group);

            let allowedSlides =
                allSlides.filter((slide) => {
                    const mediaType =
                        slide.dataset.mediaType ||
                        "image";

                    if (
                        !this.config.showVideo &&
                        (
                            mediaType === "video" ||
                            mediaType ===
                            "external_video"
                        )
                    ) {
                        return false;
                    }

                    return true;
                });


            /*
             * maxImages solo pertenece al modo slider.
             */

            if (
                this.config.imageInteraction ===
                "slider"
            ) {
                const maxImages =
                    Math.max(
                        1,
                        Number(
                            this.config.maxImages
                        ) || 1
                    );

                allowedSlides =
                    allowedSlides.slice(
                        0,
                        maxImages
                    );
            }


            const allowedSet =
                new Set(allowedSlides);

            allSlides.forEach((slide) => {
                const allowed =
                    allowedSet.has(slide);

                if (allowed) {
                    delete slide.dataset
                        .productCardExcluded;

                    slide.hidden = false;
                } else {
                    slide.dataset
                        .productCardExcluded =
                        "true";

                    slide.hidden = true;

                    slide.setAttribute(
                        "aria-hidden",
                        "true"
                    );
                }
            });


            this.rebuildPagination(
                group
            );
        }


        /* ==========================================================================
           Initial slide
           ========================================================================== */

        getInitialIndex(group) {
            const slides =
                this.getSlides(group);

            if (!slides.length) {
                return 0;
            }

            const requestedPosition =
                Math.max(
                    1,
                    Math.floor(
                        Number(
                            this.config.initialImage
                        ) || 1
                    )
                );

            return this.clamp(
                requestedPosition - 1,
                0,
                slides.length - 1
            );
        }


        /* ==========================================================================
           Media interaction
           ========================================================================== */

        syncMediaMode({
            resetPosition = true,
        } = {}) {
            if (!this.activeMediaGroup) {
                return null;
            }

            this.prepareMediaGroup(
                this.activeMediaGroup
            );

            switch (
            this.config.imageInteraction
            ) {
                case "hover":
                    return this.setupHoverMode(
                        this.activeMediaGroup
                    );

                case "static":
                    return this.setupStaticMode(
                        this.activeMediaGroup
                    );

                case "slider":
                default:
                    return this.setupSliderMode(
                        this.activeMediaGroup,
                        {
                            resetPosition,
                        }
                    );
            }
        }


        /* ==========================================================================
           Static mode
           ========================================================================== */

        setupStaticMode(group) {
            this.destroyFlickityForGroup(
                group
            );

            const slides =
                this.getSlides(group);

            const initialIndex =
                this.getInitialIndex(group);

            slides.forEach(
                (slide, index) => {
                    const isActive =
                        index === initialIndex;

                    slide.hidden =
                        !isActive;

                    slide.setAttribute(
                        "aria-hidden",
                        String(!isActive)
                    );

                    this.updateSlideTabIndex(
                        slide,
                        isActive
                    );
                }
            );

            this.activeFlickity =
                null;

            this.hideCarouselControls();

            this.updateImageCounter(
                initialIndex,
                slides.length
            );

            return null;
        }


        /* ==========================================================================
           Hover mode
           ========================================================================== */

        setupHoverMode(group) {
            this.destroyFlickityForGroup(
                group
            );

            const slides =
                this.getSlides(group);

            if (!slides.length) {
                return null;
            }

            const initialIndex =
                this.getInitialIndex(group);

            const hoverIndex =
                slides.length > 1
                    ? (
                        initialIndex + 1
                    ) % slides.length
                    : initialIndex;


            slides.forEach(
                (slide, index) => {
                    const isPrimary =
                        index === initialIndex;

                    const isSecondary =
                        index === hoverIndex &&
                        hoverIndex !== initialIndex;


                    slide.classList.toggle(
                        "product-card__slide--hover-primary",
                        isPrimary
                    );

                    slide.classList.toggle(
                        "product-card__slide--hover-secondary",
                        isSecondary
                    );


                    /*
                     * Al cargar el card:
                     *
                     * primary   -> visible
                     * secondary -> oculta
                     * resto     -> ocultas
                     */

                    slide.hidden =
                        !isPrimary;


                    slide.setAttribute(
                        "aria-hidden",
                        String(!isPrimary)
                    );


                    this.updateSlideTabIndex(
                        slide,
                        isPrimary
                    );
                }
            );


            this.activeFlickity =
                null;

            this.hideCarouselControls();

            this.updateImageCounter(
                initialIndex,
                slides.length
            );

            return null;
        }


        /* ==========================================================================
           Slider mode
           ========================================================================== */

        setupSliderMode(
            group,
            {
                resetPosition = true,
            } = {}
        ) {
            const slides =
                this.getSlides(group);

            slides.forEach((slide) => {
                slide.hidden = false;

                slide.classList.remove(
                    "product-card__slide--hover-primary",
                    "product-card__slide--hover-secondary"
                );
            });

            if (slides.length <= 1) {
                this.destroyFlickityForGroup(
                    group
                );

                this.activeFlickity =
                    null;

                this.hideCarouselControls();

                if (slides[0]) {
                    slides[0].setAttribute(
                        "aria-hidden",
                        "false"
                    );

                    this.updateSlideTabIndex(
                        slides[0],
                        true
                    );
                }

                this.updateImageCounter(
                    0,
                    slides.length
                );

                return null;
            }


            let instance =
                this.flickityByGroup.get(
                    group
                );

            if (!instance) {
                instance =
                    this.createFlickity(
                        group
                    );
            }

            this.activeFlickity =
                instance;

            if (!instance) {
                return null;
            }

            requestAnimationFrame(() => {
                if (
                    !this.root?.isConnected ||
                    this.activeMediaGroup !==
                    group
                ) {
                    return;
                }

                instance.resize?.();
                instance.reposition?.();

                if (resetPosition) {
                    const initialIndex =
                        this.getInitialIndex(
                            group
                        );

                    instance.select(
                        initialIndex,
                        false,
                        true
                    );
                }

                this.updateCarouselControls(
                    group,
                    instance
                );

                this.handleFlickitySelect(
                    group,
                    instance
                );
            });

            return instance;
        }


        /* ==========================================================================
           Flickity
           ========================================================================== */

        createFlickity(group) {
            const FlickityClass =
                this.getFlickityClass();

            if (
                typeof FlickityClass !==
                "function"
            ) {
                console.error(
                    "[ProductCard] Flickity no está disponible."
                );

                return null;
            }

            const track =
                this.getMediaTrack(group);

            const slides =
                this.getSlides(group);

            if (
                !track ||
                slides.length <= 1
            ) {
                return null;
            }

            const instance =
                new FlickityClass(
                    track,
                    {
                        cellSelector:
                            "[data-product-card-slide]:not([data-product-card-excluded])",

                        cellAlign: "left",

                        contain: true,
                        wrapAround: true,
                        draggable: true,

                        adaptiveHeight: false,

                        imagesLoaded: true,

                        prevNextButtons: false,
                        pageDots: false,

                        accessibility: true,

                        setGallerySize: false,

                        percentPosition: false,
                    }
                );


            instance.on(
                "select",
                () => {
                    this.handleFlickitySelect(
                        group,
                        instance
                    );
                }
            );


            instance.on(
                "settle",
                () => {
                    this.updateSlideAccessibility(
                        group,
                        instance
                    );
                }
            );


            this.flickityByGroup.set(
                group,
                instance
            );

            return instance;
        }


        destroyFlickityForGroup(
            group
        ) {
            const instance =
                this.flickityByGroup.get(
                    group
                );

            if (!instance) {
                return;
            }

            try {
                instance.destroy();
            } catch (error) {
                console.warn(
                    "[ProductCard] No fue posible destruir Flickity.",
                    error
                );
            }

            this.flickityByGroup.delete(
                group
            );

            if (
                this.activeFlickity ===
                instance
            ) {
                this.activeFlickity =
                    null;
            }
        }


        destroyAllFlickity() {
            for (
                const [
                    group,
                    instance,
                ] of this.flickityByGroup
            ) {
                try {
                    instance.destroy();
                } catch (error) {
                    console.warn(
                        "[ProductCard] No fue posible destruir Flickity.",
                        error
                    );
                }

                this.flickityByGroup.delete(
                    group
                );
            }

            this.activeFlickity = null;
        }


        /* ==========================================================================
           Flickity selection
           ========================================================================== */

        handleFlickitySelect(
            group,
            instance
        ) {
            const selectedIndex =
                Number(
                    instance?.selectedIndex ||
                    0
                );

            const slides =
                this.getSlides(group);

            this.updatePagination(
                group,
                selectedIndex
            );

            this.updateSlideAccessibility(
                group,
                instance
            );

            this.updateImageCounter(
                selectedIndex,
                slides.length
            );
        }


        updateSlideAccessibility(
            group,
            instance
        ) {
            const selectedIndex =
                Number(
                    instance?.selectedIndex ||
                    0
                );

            this.getSlides(group)
                .forEach(
                    (slide, index) => {
                        const isActive =
                            index ===
                            selectedIndex;

                        slide.setAttribute(
                            "aria-hidden",
                            String(!isActive)
                        );

                        this.updateSlideTabIndex(
                            slide,
                            isActive
                        );
                    }
                );
        }


        updateSlideTabIndex(
            slide,
            isActive
        ) {
            slide
                ?.querySelectorAll(
                    "a, button, input, select"
                )
                .forEach((element) => {
                    element.tabIndex =
                        isActive
                            ? 0
                            : -1;
                });
        }


        /* ==========================================================================
           Pagination
           ========================================================================== */

        rebuildPagination(group) {
            const pagination =
                group?.querySelector(
                    "[data-product-card-pagination]"
                );

            if (!pagination) {
                return;
            }

            const slides =
                this.getSlides(group);

            pagination.replaceChildren();

            if (slides.length <= 1) {
                pagination.hidden = true;
                return;
            }

            slides.forEach(
                (
                    slide,
                    index
                ) => {
                    const button =
                        document.createElement(
                            "button"
                        );

                    button.type =
                        "button";

                    button.className =
                        "product-card__pagination-dot";

                    if (index === 0) {
                        button.classList.add(
                            "is-active"
                        );
                    }

                    button.dataset
                        .productCardPaginationDot =
                        "";

                    button.dataset
                        .slideIndex =
                        String(index);

                    button.setAttribute(
                        "aria-label",
                        `Mostrar imagen ${index + 1}`
                    );

                    pagination.appendChild(
                        button
                    );
                }
            );
        }


        updatePagination(
            group,
            selectedIndex
        ) {
            group
                ?.querySelectorAll(
                    "[data-product-card-pagination-dot]"
                )
                .forEach(
                    (
                        dot,
                        index
                    ) => {
                        dot.classList.toggle(
                            "is-active",
                            index === selectedIndex
                        );
                    }
                );
        }


        /* ==========================================================================
           Carousel controls
           ========================================================================== */

        updateCarouselControls(
            group,
            instance
        ) {
            const hasMultipleSlides =
                this.getSlides(group)
                    .length > 1 &&
                Boolean(instance) &&
                this.config
                    .imageInteraction ===
                "slider";

            const previousButton =
                this.root.querySelector(
                    "[data-product-card-prev]"
                );

            const nextButton =
                this.root.querySelector(
                    "[data-product-card-next]"
                );

            if (previousButton) {
                previousButton.hidden =
                    !hasMultipleSlides;

                previousButton.setAttribute(
                    "aria-hidden",
                    String(!hasMultipleSlides)
                );
            }

            if (nextButton) {
                nextButton.hidden =
                    !hasMultipleSlides;

                nextButton.setAttribute(
                    "aria-hidden",
                    String(!hasMultipleSlides)
                );
            }


            const pagination =
                group.querySelector(
                    "[data-product-card-pagination]"
                );

            if (pagination) {
                pagination.hidden =
                    !hasMultipleSlides;

                pagination.setAttribute(
                    "aria-hidden",
                    String(!hasMultipleSlides)
                );

                this.setupPaginationPosition(
                    pagination
                );
            }
        }


        hideCarouselControls() {
            this.root
                .querySelectorAll(
                    [
                        "[data-product-card-prev]",
                        "[data-product-card-next]",
                        "[data-product-card-pagination]",
                    ].join(",")
                )
                .forEach((element) => {
                    element.hidden = true;

                    element.setAttribute(
                        "aria-hidden",
                        "true"
                    );
                });
        }


        /* ==========================================================================
           Pagination position
           ========================================================================== */

        setupPaginationPosition(
            pagination
        ) {
            const dotsBelow =
                this.scope?.classList.contains(
                    "product-card-scope--dots-below"
                );

            if (!dotsBelow) {
                pagination.classList.remove(
                    "product-card__pagination--below"
                );

                /*
                 * Si anteriormente fue movido fuera del grupo,
                 * lo regresamos a su media-group.
                 */

                const group =
                    this.activeMediaGroup;

                if (
                    group &&
                    pagination.parentElement !==
                    group
                ) {
                    group.appendChild(
                        pagination
                    );
                }

                return;
            }


            /*
             * Para estar realmente debajo de la imagen necesitamos
             * sacarlo del contenedor con overflow:hidden.
             */

            if (
                this.media &&
                pagination.parentElement !==
                this.media.parentElement
            ) {
                this.media.insertAdjacentElement(
                    "afterend",
                    pagination
                );
            }

            pagination.classList.add(
                "product-card__pagination--below"
            );
        }


        /* ==========================================================================
           Image counter
           ========================================================================== */

        getOrCreateImageCounter() {
            let counter =
                this.root.querySelector(
                    "[data-product-card-image-counter]"
                );

            if (counter) {
                return counter;
            }

            counter =
                document.createElement(
                    "span"
                );

            counter.className =
                "product-card__image-counter";

            counter.dataset
                .productCardImageCounter =
                "";

            this.media?.appendChild(
                counter
            );

            return counter;
        }


        updateImageCounter(
            selectedIndex,
            total
        ) {
            const counter =
                this.getOrCreateImageCounter();

            if (!counter) {
                return;
            }

            if (total <= 1) {
                counter.hidden = true;
                return;
            }

            counter.hidden = false;

            counter.textContent =
                `${selectedIndex + 1}/${total}`;
        }


        /* ==========================================================================
           Activate media group
           ========================================================================== */

        activateMediaGroup(
            nextGroup,
            {
                resetPosition = true,
            } = {}
        ) {
            if (!nextGroup) {
                return null;
            }

            this.mediaGroups.forEach(
                (group) => {
                    const isActive =
                        group === nextGroup;

                    group.hidden =
                        !isActive;

                    group.classList.toggle(
                        "is-active",
                        isActive
                    );

                    this.updateGroupTabIndex(
                        group,
                        isActive
                    );
                }
            );

            this.activeMediaGroup =
                nextGroup;

            return this.syncMediaMode({
                resetPosition,
            });
        }


        updateGroupTabIndex(
            group,
            isActive
        ) {
            group
                .querySelectorAll(
                    "a, button, input, select"
                )
                .forEach((element) => {
                    if (isActive) {
                        element.removeAttribute(
                            "tabindex"
                        );
                    } else {
                        element.setAttribute(
                            "tabindex",
                            "-1"
                        );
                    }
                });
        }


        /* ==========================================================================
           Swatches
           ========================================================================== */

        getSwatches() {
            return Array.from(
                this.root.querySelectorAll(
                    "[data-product-card-swatch]"
                )
            );
        }


        setupSwatches() {
            const swatches =
                this.getSwatches();

            if (!swatches.length) {
                return;
            }

            this.selectedSwatch =
                swatches.find(
                    (swatch) =>
                        swatch.classList.contains(
                            "is-active"
                        )
                ) ||
                swatches[0];


            if (
                this.selectedSwatch &&
                !this.selectedSwatch
                    .classList.contains(
                        "is-active"
                    )
            ) {
                this.selectedSwatch
                    .classList.add(
                        "is-active"
                    );

                this.selectedSwatch
                    .setAttribute(
                        "aria-pressed",
                        "true"
                    );
            }


            const maxSwatches =
                Math.max(
                    1,
                    Number(
                        this.config.maxSwatches
                    ) || 1
                );


            swatches.forEach(
                (
                    swatch,
                    index
                ) => {
                    swatch.hidden =
                        index >= maxSwatches;
                }
            );


            const remainingCount =
                Math.max(
                    0,
                    swatches.length -
                    maxSwatches
                );


            const more =
                this.root.querySelector(
                    "[data-product-card-swatches-more]"
                );


            if (more) {
                const shouldShow =
                    this.config
                        .showRemainingSwatches &&
                    remainingCount > 0;

                more.hidden =
                    !shouldShow;

                more.textContent =
                    shouldShow
                        ? `+${remainingCount}`
                        : "";
            }
        }


        selectSwatch(swatch) {
            if (
                !swatch ||
                swatch ===
                this.selectedSwatch
            ) {
                return;
            }

            this.getSwatches()
                .forEach((item) => {
                    const active =
                        item === swatch;

                    item.classList.toggle(
                        "is-active",
                        active
                    );

                    item.setAttribute(
                        "aria-pressed",
                        String(active)
                    );
                });


            this.selectedSwatch =
                swatch;

            this.hoveredSwatch =
                null;

            this.updateCardFromSwatch(
                swatch,
                {
                    changeMediaGroup: true,
                    emit: true,
                }
            );
        }


        /* ==========================================================================
           Swatch hover
           ========================================================================== */

        previewSwatch(swatch) {
            if (
                !this.config
                    .swatchHoverMedia ||
                this.isMobile() ||
                !swatch
            ) {
                return;
            }

            const productId =
                swatch.dataset.productId;

            const mediaGroup =
                this.findMediaGroupByProductId(
                    productId
                );

            if (!mediaGroup) {
                return;
            }

            this.hoveredSwatch =
                swatch;

            this.activateMediaGroup(
                mediaGroup,
                {
                    resetPosition: true,
                }
            );
        }


        restoreSelectedSwatchMedia() {
            if (!this.selectedSwatch) {
                return;
            }

            this.hoveredSwatch =
                null;

            const mediaGroup =
                this.findMediaGroupByProductId(
                    this.selectedSwatch.dataset
                        .productId
                );

            if (!mediaGroup) {
                return;
            }

            this.activateMediaGroup(
                mediaGroup,
                {
                    resetPosition: true,
                }
            );
        }


        /* ==========================================================================
           Update card from swatch
           ========================================================================== */

        updateCardFromSwatch(
            swatch,
            {
                changeMediaGroup = true,
                emit = true,
            } = {}
        ) {
            if (!swatch) {
                return;
            }

            const productId =
                this.normalizeProductId(
                    swatch.dataset.productId
                );

            const productHandle =
                this.normalizeString(
                    swatch.dataset
                        .productHandle
                );

            const productUrl =
                this.normalizeString(
                    swatch.dataset
                        .productUrl
                );

            const title =
                this.normalizeString(
                    swatch.dataset.title
                );

            const color =
                this.normalizeString(
                    swatch.dataset.color
                );


            this.root.dataset
                .activeProductId =
                productId;

            this.root.dataset
                .activeHandle =
                productHandle;

            this.root.dataset
                .activeColor =
                color;


            this.updateProductLinks(
                productUrl
            );

            this.updateTitle(
                title
            );

            this.updatePrice(
                swatch
            );

            this.updateBadge(
                productId
            );

            this.updateCTA(
                swatch
            );

            this.updateWishlist(
                swatch
            );


            if (changeMediaGroup) {
                const mediaGroup =
                    this.findMediaGroupByProductId(
                        productId
                    );

                if (mediaGroup) {
                    this.activateMediaGroup(
                        mediaGroup,
                        {
                            resetPosition: true,
                        }
                    );
                }
            }


            if (emit) {
                this.root.dispatchEvent(
                    new CustomEvent(
                        "product-card:color-change",
                        {
                            bubbles: true,

                            detail: {
                                card: this,
                                productId,
                                productHandle,
                                productUrl,
                                color,
                                swatch,
                            },
                        }
                    )
                );
            }
        }


        /* ==========================================================================
           Product links
           ========================================================================== */

        updateProductLinks(
            productUrl
        ) {
            if (!productUrl) {
                return;
            }

            const trackedUrl =
                this.buildTrackedUrl(
                    productUrl
                );


            this.root
                .querySelectorAll(
                    "[data-product-card-link]"
                )
                .forEach((link) => {
                    link.href =
                        trackedUrl;
                });
        }

        applyTrackingToExistingLinks() {
            this.root
                .querySelectorAll(
                    "[data-product-card-link]"
                )
                .forEach((link) => {
                    const href =
                        link.getAttribute(
                            "href"
                        );

                    if (!href) {
                        return;
                    }

                    link.href =
                        this.buildTrackedUrl(
                            href
                        );
                });
        }


        /* ==========================================================================
           Title
           ========================================================================== */

        updateTitle(title) {
            if (!title) {
                return;
            }

            const titleElement =
                this.root.querySelector(
                    "[data-product-card-title]"
                );

            if (!titleElement) {
                return;
            }

            titleElement.textContent =
                title;

            titleElement.title =
                title;
        }


        /* ==========================================================================
           Price
           ========================================================================== */

        normalizeFormattedPrice(
            value
        ) {
            return this
                .normalizeString(value)
                .replace(
                    /\$\s+/g,
                    "$"
                );
        }


        updatePrice(swatch) {
            const priceVaries =
                swatch.dataset
                    .priceVaries ===
                "true";

            const formattedPrice =
                this.normalizeFormattedPrice(
                    swatch.dataset
                        .formattedPrice
                );

            const formattedPriceMin =
                this.normalizeFormattedPrice(
                    swatch.dataset
                        .formattedPriceMin
                );

            const comparePrice =
                Number(
                    swatch.dataset
                        .comparePrice ||
                    0
                );

            const formattedComparePrice =
                this.normalizeFormattedPrice(
                    swatch.dataset
                        .formattedComparePrice
                );

            const hasDiscount =
                swatch.dataset
                    .hasDiscount ===
                "true" ||
                comparePrice > 0;


            const currentPriceText =
                priceVaries
                    ? `Desde ${formattedPriceMin}`
                    : formattedPrice;


            this.root
                .querySelectorAll(
                    "[data-product-card-current-price]"
                )
                .forEach(
                    (element) => {
                        element.textContent =
                            currentPriceText;

                        element.dataset
                            .priceVaries =
                            String(priceVaries);

                        element.classList.toggle(
                            "is-sale",
                            hasDiscount
                        );
                    }
                );


            this.root
                .querySelectorAll(
                    "[data-product-card-compare-price]"
                )
                .forEach(
                    (element) => {
                        element.hidden =
                            !hasDiscount;

                        element.textContent =
                            hasDiscount
                                ? formattedComparePrice
                                : "";
                    }
                );
        }


        /* ==========================================================================
           Badge
           ========================================================================== */

        updateBadge(productId) {
            const badgeContainer =
                this.root.querySelector(
                    "[data-product-card-badge]"
                );

            if (!badgeContainer) {
                return;
            }

            const normalizedId =
                this.normalizeProductId(
                    productId
                );

            const template =
                Array.from(
                    this.root.querySelectorAll(
                        "[data-product-card-badge-template]"
                    )
                ).find(
                    (item) =>
                        this.normalizeProductId(
                            item.dataset.productId
                        ) === normalizedId
                );


            if (!template) {
                badgeContainer
                    .replaceChildren();

                return;
            }


            badgeContainer
                .replaceChildren(
                    template.content.cloneNode(
                        true
                    )
                );
        }


        /* ==========================================================================
           Wishlist
           ========================================================================== */

        updateWishlist(swatch) {
            const button =
                this.root.querySelector(
                    ".c-button-wishlist"
                );

            if (!button) {
                return;
            }

            button.dataset
                .wishlist__id =
                swatch.dataset
                    .productId || "";

            button.dataset
                .wishlist__variant =
                swatch.dataset
                    .firstVariantId || "";

            button.dataset
                .wishlist__handle =
                swatch.dataset
                    .productHandle || "";

            button.dataset
                .productname =
                swatch.dataset
                    .title || "";

            button.dataset
                .productid =
                swatch.dataset
                    .productId || "";

            button.dataset
                .producturl =
                swatch.dataset
                    .productUrl || "";

            button.dataset
                .imageurl =
                swatch.dataset
                    .imageUrl || "";

            button.dataset.price =
                swatch.dataset.price ||
                "";
        }


        /* ==========================================================================
           CTA
           ========================================================================== */

        getCTA() {
            return (
                this.root.querySelector(
                    "[data-product-card-cta]"
                ) ||
                null
            );
        }


        setupCTA() {
            const cta =
                this.getCTA();

            if (!cta) {
                return;
            }

            /*
             * Guardamos su posición original una sola vez.
             */

            if (!this.ctaOriginalParent) {
                this.ctaOriginalParent =
                    cta.parentElement;

                this.ctaOriginalNextSibling =
                    cta.nextSibling;
            }


            const overlay =
                this.scope?.classList.contains(
                    "product-card-scope--cta-overlay"
                );


            if (
                overlay &&
                this.media &&
                cta.parentElement !==
                this.media
            ) {
                this.media.appendChild(
                    cta
                );
            }


            if (
                !overlay &&
                this.ctaOriginalParent &&
                cta.parentElement !==
                this.ctaOriginalParent
            ) {
                if (
                    this.ctaOriginalNextSibling &&
                    this.ctaOriginalNextSibling
                        .parentElement ===
                    this.ctaOriginalParent
                ) {
                    this.ctaOriginalParent
                        .insertBefore(
                            cta,
                            this.ctaOriginalNextSibling
                        );
                } else {
                    this.ctaOriginalParent
                        .appendChild(
                            cta
                        );
                }
            }


            const text =
                cta.querySelector(
                    "[data-product-card-cta-text]"
                );

            if (text) {
                text.textContent =
                    cta.disabled
                        ? "Agotado"
                        : (
                            this.config.ctaText ||
                            "Agregar"
                        );
            }


            const icon =
                cta.querySelector(
                    "[data-product-card-cta-icon]"
                );

            if (
                icon &&
                this.config.ctaIcon
            ) {
                icon.innerHTML =
                    this.config.ctaIcon;
            }
        }


        updateCTA(swatch) {
            const cta =
                this.getCTA();

            if (!cta) {
                return;
            }

            const available =
                swatch.dataset.available ===
                "true";


            cta.dataset.productId =
                swatch.dataset
                    .productId || "";

            cta.dataset.productHandle =
                swatch.dataset
                    .productHandle || "";

            cta.dataset.productUrl =
                swatch.dataset
                    .productUrl || "";

            cta.dataset.quickBuyUrl =
                swatch.dataset
                    .quickBuyUrl || "";

            cta.disabled =
                !available;


            const text =
                cta.querySelector(
                    "[data-product-card-cta-text]"
                );

            if (text) {
                text.textContent =
                    available
                        ? (
                            this.config.ctaText ||
                            "Agregar"
                        )
                        : "Agotado";
            }
        }


        openQuickBuy(cta) {
            if (
                !cta ||
                cta.disabled
            ) {
                return;
            }

            const productId =
                this.normalizeProductId(
                    cta.dataset.productId
                );

            const productUrl =
                this.normalizeString(
                    cta.dataset.productUrl
                );

            const source =
                this.normalizeString(
                    this.root.dataset
                        .productSource || ""
                );

            const track =
                this.getTrackingType();

            const attributionKey =
                this.getAttributionKey();

            const context = {
                source,
                track,
                attributionKey,

                placementId:
                    this.normalizeString(
                        this.root.dataset
                            .nostoPlacementId || ""
                    ),

                resultId:
                    this.normalizeString(
                        this.root.dataset
                            .nostoResultId || ""
                    ),

                divId:
                    this.normalizeString(
                        this.root.dataset
                            .nostoDivId || ""
                    ),

                originalProductId:
                    this.normalizeProductId(
                        this.root.dataset
                            .nostoOriginalProductId ||
                        productId
                    ),
            };

            const detail = {
                card: this,
                root: this.root,

                productId,

                productHandle:
                    this.normalizeString(
                        cta.dataset.productHandle
                    ),

                productUrl,

                quickBuyUrl:
                    this.normalizeString(
                        cta.dataset.quickBuyUrl
                    ),

                button: cta,

                hit: {
                    productId,
                    url: productUrl,
                },

                source,
                track,
                attributionKey,
                context,
            };

            document.dispatchEvent(
                new CustomEvent(
                    "nosto:quick-buy:open",
                    {
                        bubbles: true,
                        detail,
                    }
                )
            );
        }


        /* ==========================================================================
           Floating price icon
           ========================================================================== */

        setupFloatingPriceIcon() {
            const container =
                this.root.querySelector(
                    "[data-product-card-floating-price-icon]"
                );

            if (!container) {
                return;
            }

            /*
             * Guardamos el SVG default generado por Liquid
             * para poder restaurarlo si el setting custom queda vacío.
             */

            if (
                !container.dataset.defaultIcon
            ) {
                container.dataset.defaultIcon =
                    container.innerHTML;
            }

            if (
                this.config.floatingPriceIcon
            ) {
                container.innerHTML =
                    this.config.floatingPriceIcon;
            } else {
                container.innerHTML =
                    container.dataset.defaultIcon;
            }
        }


        /* ==========================================================================
           Click
           ========================================================================== */

        handleClick(event) {
            const swatch =
                event.target.closest(
                    "[data-product-card-swatch]"
                );

            if (
                swatch &&
                this.root.contains(swatch)
            ) {
                event.preventDefault();

                this.selectSwatch(
                    swatch
                );

                return;
            }


            const previous =
                event.target.closest(
                    "[data-product-card-prev]"
                );

            if (
                previous &&
                this.root.contains(previous)
            ) {
                event.preventDefault();
                event.stopPropagation();

                this.activeFlickity
                    ?.previous(true);

                return;
            }


            const next =
                event.target.closest(
                    "[data-product-card-next]"
                );

            if (
                next &&
                this.root.contains(next)
            ) {
                event.preventDefault();
                event.stopPropagation();

                this.activeFlickity
                    ?.next(true);

                return;
            }


            const dot =
                event.target.closest(
                    "[data-product-card-pagination-dot]"
                );

            if (
                dot &&
                this.root.contains(dot)
            ) {
                event.preventDefault();

                const index =
                    Number(
                        dot.dataset.slideIndex
                    );

                if (
                    Number.isFinite(index)
                ) {
                    this.activeFlickity
                        ?.select(index);
                }

                return;
            }


            const cta =
                event.target.closest(
                    "[data-product-card-cta]"
                );

            if (
                cta &&
                this.root.contains(cta)
            ) {
                event.preventDefault();

                this.openQuickBuy(
                    cta
                );
            }
        }


        /* ==========================================================================
           Swatch hover events
           ========================================================================== */

        handleMouseOver(event) {
            const swatch =
                event.target.closest(
                    "[data-product-card-swatch]"
                );

            if (
                swatch &&
                this.root.contains(
                    swatch
                )
            ) {
                this.previewSwatch(
                    swatch
                );

                return;
            }


            /*
             * Hover mode de imágenes.
             */

            if (
                this.config
                    .imageInteraction !==
                "hover" ||
                this.isMobile()
            ) {
                return;
            }

            const media =
                event.target.closest(
                    "[data-product-card-media]"
                );

            if (
                media !== this.media ||
                !this.activeMediaGroup
            ) {
                return;
            }

            this.setHoverImageState(
                true
            );
        }


        handleMouseOut(event) {
            const swatch =
                event.target.closest(
                    "[data-product-card-swatch]"
                );

            if (
                swatch &&
                this.root.contains(
                    swatch
                )
            ) {
                const nextTarget =
                    event.relatedTarget;

                if (
                    nextTarget &&
                    swatch.contains(
                        nextTarget
                    )
                ) {
                    return;
                }

                this.restoreSelectedSwatchMedia();

                return;
            }


            if (
                this.config
                    .imageInteraction !==
                "hover" ||
                this.isMobile()
            ) {
                return;
            }


            const media =
                event.target.closest(
                    "[data-product-card-media]"
                );

            if (media !== this.media) {
                return;
            }


            const relatedTarget =
                event.relatedTarget;

            if (
                relatedTarget &&
                this.media.contains(
                    relatedTarget
                )
            ) {
                return;
            }


            this.setHoverImageState(
                false
            );
        }


        setHoverImageState(
            hoverActive
        ) {
            const group =
                this.activeMediaGroup;

            if (!group) {
                return;
            }

            const primary =
                group.querySelector(
                    ".product-card__slide--hover-primary"
                );

            const secondary =
                group.querySelector(
                    ".product-card__slide--hover-secondary"
                );

            if (
                !primary ||
                !secondary
            ) {
                return;
            }

            primary.hidden =
                hoverActive;

            secondary.hidden =
                !hoverActive;

            primary.setAttribute(
                "aria-hidden",
                String(hoverActive)
            );

            secondary.setAttribute(
                "aria-hidden",
                String(!hoverActive)
            );

            this.updateSlideTabIndex(
                primary,
                !hoverActive
            );

            this.updateSlideTabIndex(
                secondary,
                hoverActive
            );
        }


        /* ==========================================================================
           Keyboard swatches
           ========================================================================== */

        handleKeydown(event) {
            const swatch =
                event.target.closest(
                    "[data-product-card-swatch]"
                );

            if (!swatch) {
                return;
            }

            if (
                event.key !== "ArrowLeft" &&
                event.key !== "ArrowRight"
            ) {
                return;
            }

            const swatches =
                this.getSwatches().filter(
                    (item) =>
                        !item.hidden &&
                        !item.disabled
                );

            const currentIndex =
                swatches.indexOf(
                    swatch
                );

            if (currentIndex < 0) {
                return;
            }

            event.preventDefault();

            const direction =
                event.key ===
                    "ArrowRight"
                    ? 1
                    : -1;

            const nextIndex =
                (
                    currentIndex +
                    direction +
                    swatches.length
                ) %
                swatches.length;

            swatches[nextIndex]
                .focus();

            this.selectSwatch(
                swatches[nextIndex]
            );
        }


        /* ==========================================================================
           Refresh
           ========================================================================== */

        refresh() {
            if (
                !this.root ||
                !this.root.isConnected
            ) {
                return;
            }

            this.refreshConfig();

            this.setupSwatches();
            this.applyTrackingToExistingLinks();
            this.setupCTA();
            this.setupFloatingPriceIcon();

            const activeGroup =
                this.getActiveMediaGroup() ||
                this.activeMediaGroup ||
                this.mediaGroups[0];

            if (!activeGroup) {
                return;
            }

            this.activateMediaGroup(
                activeGroup,
                {
                    resetPosition: false,
                }
            );


            requestAnimationFrame(
                () => {
                    this.activeFlickity
                        ?.resize?.();

                    this.activeFlickity
                        ?.reposition?.();
                }
            );
        }


        handleResize() {
            this.activeFlickity
                ?.resize?.();
        }


        /* ==========================================================================
           Destroy
           ========================================================================== */

        destroy() {
            if (this.isDestroyed) {
                return;
            }

            this.root.removeEventListener(
                "click",
                this.boundHandleClick
            );

            this.root.removeEventListener(
                "mouseover",
                this.boundHandleMouseOver
            );

            this.root.removeEventListener(
                "mouseout",
                this.boundHandleMouseOut
            );

            this.root.removeEventListener(
                "keydown",
                this.boundHandleKeydown
            );

            document.removeEventListener(
                "theme:resize:width",
                this.boundHandleResize
            );

            global.removeEventListener(
                "resize",
                this.boundHandleResize
            );

            this.destroyAllFlickity();

            delete this.root[
                INSTANCE_KEY
            ];

            this.isDestroyed = true;
            this.isInitialized = false;
        }
    }


    /* ==========================================================================
       Global initializer
       ========================================================================== */

    const observedCards =
        new WeakSet();

    let mutationObserver =
        null;

    let resizeObserver =
        null;

    let intersectionObserver =
        null;

    let globalStarted =
        false;


    function isCardMeasurable(
        root
    ) {
        if (
            !(root instanceof Element) ||
            !root.isConnected
        ) {
            return false;
        }

        if (
            root.closest("[hidden]")
        ) {
            return false;
        }

        const style =
            global.getComputedStyle(
                root
            );

        if (
            style.display === "none" ||
            style.visibility ===
            "hidden"
        ) {
            return false;
        }

        return (
            root.getBoundingClientRect()
                .width > 0
        );
    }


    function getOrCreateInstance(
        root
    ) {
        if (
            !(root instanceof Element)
        ) {
            return null;
        }

        if (root[INSTANCE_KEY]) {
            return root[
                INSTANCE_KEY
            ];
        }

        const instance =
            new ProductCard(root);

        root[INSTANCE_KEY] =
            instance;

        instance.initialize();

        return instance;
    }


    function initializeCard(
        root
    ) {
        if (
            !isCardMeasurable(root)
        ) {
            return null;
        }

        try {
            const instance =
                getOrCreateInstance(
                    root
                );

            instance?.refresh();

            return instance;
        } catch (error) {
            console.error(
                "[ProductCard] No fue posible inicializar el card.",
                {
                    root,
                    error,
                }
            );

            return null;
        }
    }


    function findCards(scope) {
        if (
            !(scope instanceof Element) &&
            !(scope instanceof Document) &&
            !(
                scope instanceof
                DocumentFragment
            )
        ) {
            return [];
        }

        const cards = [];

        if (
            scope instanceof Element &&
            scope.matches(
                CARD_SELECTOR
            )
        ) {
            cards.push(scope);
        }

        cards.push(
            ...scope.querySelectorAll(
                CARD_SELECTOR
            )
        );

        return Array.from(
            new Set(cards)
        );
    }


    function observeCard(root) {
        if (
            !(root instanceof Element) ||
            observedCards.has(root)
        ) {
            return;
        }

        observedCards.add(root);

        resizeObserver
            ?.observe(root);

        if (intersectionObserver) {
            intersectionObserver
                .observe(root);

            return;
        }

        if (isCardMeasurable(root)) {
            initializeCard(root);
        }
    }


    /* ==========================================================================
       Public API
       ========================================================================== */

    global.initializeProductCards = (
        scope = document
    ) => {
        const cards =
            findCards(scope);

        cards.forEach(
            observeCard
        );

        return cards
            .map(
                (root) =>
                    root[INSTANCE_KEY] || null
            )
            .filter(Boolean);
    };


    global.refreshProductCards = (
        scope = document
    ) => {
        const cards =
            findCards(scope);

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                cards.forEach(
                    (root) => {
                        observeCard(root);

                        initializeCard(
                            root
                        );
                    }
                );
            });
        });
    };


    /* ==========================================================================
       Global observers
       ========================================================================== */

    function startGlobalInitializer() {
        if (globalStarted) {
            return;
        }

        globalStarted = true;


        /*
         * ResizeObserver
         */

        if (
            "ResizeObserver" in
            global
        ) {
            resizeObserver =
                new ResizeObserver(
                    (entries) => {
                        entries.forEach(
                            (entry) => {
                                const root =
                                    entry.target;

                                const instance =
                                    root[
                                    INSTANCE_KEY
                                    ];

                                instance
                                    ?.activeFlickity
                                    ?.resize?.();
                            }
                        );
                    }
                );
        }


        /*
         * IntersectionObserver
         *
         * Evita inicializar Flickity dentro de tabs ocultos
         * o cards muy lejos del viewport.
         */

        if (
            "IntersectionObserver" in
            global
        ) {
            intersectionObserver =
                new IntersectionObserver(
                    (entries) => {
                        entries.forEach(
                            (entry) => {
                                if (
                                    !entry.isIntersecting &&
                                    entry
                                        .intersectionRatio <=
                                    0
                                ) {
                                    return;
                                }

                                initializeCard(
                                    entry.target
                                );
                            }
                        );
                    },
                    {
                        rootMargin:
                            "300px 0px 300px 0px",

                        threshold: 0.01,
                    }
                );
        }


        /*
         * MutationObserver
         *
         * Solo detecta cards agregados por AJAX / Nosto.
         * No observa class/style/hidden/open.
         */
        mutationObserver =
            new MutationObserver(
                (mutations) => {
                    mutations.forEach(
                        (mutation) => {
                            mutation.addedNodes
                                .forEach(
                                    (node) => {
                                        if (
                                            !(
                                                node instanceof
                                                Element
                                            ) &&
                                            !(
                                                node instanceof
                                                DocumentFragment
                                            )
                                        ) {
                                            return;
                                        }

                                        findCards(node)
                                            .forEach(
                                                observeCard
                                            );
                                    }
                                );
                        }
                    );
                }
            );


        mutationObserver.observe(
            document.documentElement,
            {
                childList: true,
                subtree: true,
            }
        );

        global.initializeProductCards(
            document
        );
    }


    /* ==========================================================================
       Shopify Theme Editor
       ========================================================================== */

    document.addEventListener(
        "shopify:section:load",
        (event) => {
            global.initializeProductCards(
                event.target
            );
        }
    );


    document.addEventListener(
        "shopify:section:reorder",
        () => {
            global.refreshProductCards(
                document
            );
        }
    );


    /* ==========================================================================
       Start
       ========================================================================== */

    if (
        document.readyState ===
        "loading"
    ) {
        document.addEventListener(
            "DOMContentLoaded",
            startGlobalInitializer,
            {
                once: true,
            }
        );
    } else {
        startGlobalInitializer();
    }


    global.ProductCard =
        ProductCard;

})(window);